/**
 * SSOT: כבודה כלולה מנתוני טיסה LDS (linkedFlights / legs / segments) — ללא המלצות UI.
 * לשימוש בכרטיסי תוצאות, פרופיל, תשלום, תוספות וסיכומי הזמנה.
 */

import { getDepRetFlightPairs } from "./vacationPackagesLinkedFlights";

function unwrapLegLike(legLike) {
  if (legLike == null) return null;
  return Array.isArray(legLike) ? legLike[0] : legLike;
}

function isTruthyFlag(value) {
  if (value === true) return true;
  if (typeof value === "number") return value > 0;
  if (typeof value !== "string") return false;
  const t = value.trim().toLowerCase();
  return t === "true" || t === "1" || t === "yes" || t === "included";
}

/** @param {unknown} value */
function parseWeightKg(value) {
  if (value == null || value === "") return null;
  if (typeof value === "number") return value > 0 ? value : null;
  if (typeof value === "string") {
    const m = value.match(/(\d+(?:\.\d+)?)/);
    if (!m) return null;
    const n = Number.parseFloat(m[1]);
    return Number.isFinite(n) && n > 0 ? n : null;
  }
  if (typeof value === "object") {
    const keys = [
      "kg",
      "weight",
      "maxWeight",
      "allowance",
      "value",
      "amount",
      "max",
      "checked",
      "carryOn",
      "hand",
    ];
    for (const key of keys) {
      const parsed = parseWeightKg(value?.[key]);
      if (parsed != null) return parsed;
    }
  }
  return null;
}

/** אובייקטים שנסרקים לכל כיוון (leg + צאצאי segments) */
function collectReadableNodes(leg, wrapper) {
  const nodes = [];
  const push = (obj, prefix) => {
    if (!obj || typeof obj !== "object") return;
    nodes.push({ obj, prefix });
  };

  const L = unwrapLegLike(leg);
  push(L, "leg");

  const wSegsRaw = wrapper?.segments;
  const lSegsRaw = L?.segments;
  const fromSeg = wSegsRaw ?? lSegsRaw;
  const segs = Array.isArray(fromSeg) ? fromSeg : fromSeg ? [fromSeg] : [];
  segs.forEach((s, i) => push(s, `segment[${i}]`));

  push(wrapper && typeof wrapper === "object" ? wrapper : null, "flightItem");
  return nodes;
}

function readMaxHandKgWithPath(nodes) {
  let maxKg = 0;
  let sourcePath = null;
  const fields = [
    "maxHandLuggageWeight",
    "handLuggageWeight",
    "carryOnWeight",
    "carryOnKg",
    "carryOn",
    ["baggageInfo", "carryOn"],
    ["includedBaggage", "carryOn"],
    ["luggage", "carryOn"],
  ];
  for (const { obj, prefix } of nodes) {
    for (const f of fields) {
      let raw;
      let pathTail;
      if (typeof f === "string") {
        raw = obj[f];
        pathTail = f;
      } else {
        raw = obj?.[f[0]]?.[f[1]];
        pathTail = `${f[0]}.${f[1]}`;
      }
      const kg = parseWeightKg(raw);
      if (kg != null && kg >= maxKg) {
        maxKg = kg;
        sourcePath = `${prefix}.${pathTail}`;
      }
    }
  }
  return maxKg > 0 ? { kg: maxKg, sourcePath } : { kg: null, sourcePath: null };
}

function readMaxCheckedKgWithPath(nodes) {
  let maxKg = 0;
  let sourcePath = null;
  const fields = [
    "maxCheckedLuggageWeight",
    "checkedLuggageWeight",
    "checkedBaggage",
    "checkedBaggageWeight",
    "checkedBaggageKg",
    "baggageAllowance",
    ["baggageInfo", "checked"],
    ["includedBaggage", "checked"],
    ["luggage", "checked"],
  ];
  for (const { obj, prefix } of nodes) {
    for (const f of fields) {
      let raw;
      let pathTail;
      if (typeof f === "string") {
        raw = obj[f];
        pathTail = f;
      } else {
        raw = obj?.[f[0]]?.[f[1]];
        pathTail = `${f[0]}.${f[1]}`;
      }
      const kg = parseWeightKg(raw);
      if (kg != null && kg >= maxKg) {
        maxKg = kg;
        sourcePath = `${prefix}.${pathTail}`;
      }
    }
  }
  return maxKg > 0 ? { kg: maxKg, sourcePath } : { kg: null, sourcePath: null };
}

function anyCarryFlag(nodes) {
  for (const { obj } of nodes) {
    if (
      isTruthyFlag(obj?.handLuggageIncluded) ||
      isTruthyFlag(obj?.carryOnIncluded) ||
      isTruthyFlag(obj?.includedBaggage?.carryOnIncluded)
    )
      return true;
  }
  return false;
}

function anyCheckedFlag(nodes) {
  for (const { obj } of nodes) {
    if (
      isTruthyFlag(obj?.checkedLuggageIncluded) ||
      isTruthyFlag(obj?.checkedBaggageIncluded) ||
      isTruthyFlag(obj?.includedBaggage?.checkedIncluded)
    )
      return true;
  }
  return false;
}

function mergeDirectionsPair(depPair, retPair) {
  const parts = [];

  let carryKg = null;
  let carryPath = null;
  let checkedKg = null;
  let checkedPath = null;
  let carryFlag = false;
  let checkedFlag = false;

  for (const pair of [depPair, retPair]) {
    if (!pair) continue;
    carryFlag = carryFlag || pair.carryFlag;
    checkedFlag = checkedFlag || pair.checkedFlag;

    if (pair.carryKg != null && (carryKg == null || pair.carryKg > carryKg)) {
      carryKg = pair.carryKg;
      carryPath = pair.carryKgPath;
    }
    if (
      pair.checkedKg != null &&
      (checkedKg == null || pair.checkedKg > checkedKg)
    ) {
      checkedKg = pair.checkedKg;
      checkedPath = pair.checkedKgPath;
    }
  }

  if (carryFlag) parts.push(`carryFlag(dep/ret merged)`);
  if (checkedFlag) parts.push(`checkedFlag(dep/ret merged)`);
  if (carryKg != null) parts.push(carryPath || "carryKg");
  if (checkedKg != null) parts.push(checkedPath || "checkedKg");

  return {
    carryOnWeightKg: carryKg,
    checkedBaggageWeightKg: checkedKg,
    carryOnIncluded: carryKg != null || carryFlag,
    checkedBaggageIncluded: checkedKg != null || checkedFlag,
    carryOnSourcePath: carryPath,
    checkedSourcePath: checkedPath,
    sourcePath:
      parts.length > 0
        ? `linkedFlights.flight (max of legs): ${parts.join(" | ")}`
        : null,
  };
}

function analyzeOneDirection(leg, wrapper) {
  const nodes = collectReadableNodes(leg, wrapper);
  const hand = readMaxHandKgWithPath(nodes);
  const chk = readMaxCheckedKgWithPath(nodes);
  return {
    carryKg: hand.kg,
    carryKgPath: hand.sourcePath,
    checkedKg: chk.kg,
    checkedKgPath: chk.sourcePath,
    carryFlag: anyCarryFlag(nodes),
    checkedFlag: anyCheckedFlag(nodes),
  };
}

function buildHebrewCopy(merged) {
  const { carryOnIncluded, checkedBaggageIncluded, carryOnWeightKg, checkedBaggageWeightKg } =
    merged;

  const bullets = [];
  if (carryOnIncluded) {
    bullets.push(
      carryOnWeightKg != null
        ? `טרולי כלול ${carryOnWeightKg} ק"ג`
        : "טרולי כלול"
    );
  }
  if (checkedBaggageIncluded) {
    bullets.push(
      checkedBaggageWeightKg != null
        ? `מזוודה כלולה ${checkedBaggageWeightKg} ק"ג`
        : "מזוודה כלולה"
    );
  }
  if (bullets.length === 0) {
    bullets.push("ללא כבודה");
  }

  let summaryLabel = "";
  if (merged.carryOnIncluded && merged.checkedBaggageIncluded) {
    const trol =
      merged.carryOnWeightKg != null
        ? `טרולי ${merged.carryOnWeightKg} ק"ג`
        : "טרולי";
    const suit =
      merged.checkedBaggageWeightKg != null
        ? `מזוודה ${merged.checkedBaggageWeightKg} ק"ג`
        : "מזוודה";
    summaryLabel = `כולל ${trol} + ${suit}`;
  } else if (merged.carryOnIncluded) {
    summaryLabel =
      merged.carryOnWeightKg != null
        ? `כולל טרולי ${merged.carryOnWeightKg} ק"ג`
        : "כולל טרולי";
  } else if (merged.checkedBaggageIncluded) {
    summaryLabel =
      merged.checkedBaggageWeightKg != null
        ? `כולל מזוודה ${merged.checkedBaggageWeightKg} ק"ג`
        : "כולל מזוודה";
  } else {
    summaryLabel = "ללא כבודה כלולה בכרטיס";
  }

  return { bullets, summaryLabel };
}

function resolveLinkedFlightsRoot(input) {
  if (!input || typeof input !== "object") return null;
  if (input.linkedFlights && typeof input.linkedFlights === "object") {
    return input.linkedFlights;
  }
  if (
    input.flight ||
    (Array.isArray(input) &&
      input.length > 0 &&
      typeof input[0] === "object" &&
      (input[0].flight || input[0].leg))
  ) {
    return input;
  }
  return input.linkedFlights ?? null;
}

/**
 * קריאה לכיוון בודד (הלוך או חזור) לתצוגה בפרטי טיסה.
 */
export function resolveVacationPackageIncludedBaggageForLeg(leg, wrapper = null) {
  const one = analyzeOneDirection(leg, wrapper);
  const carryOnIncluded = one.carryKg != null || one.carryFlag;
  const checkedBaggageIncluded = one.checkedKg != null || one.checkedFlag;
  const merged = {
    carryOnIncluded,
    checkedBaggageIncluded,
    carryOnWeightKg: one.carryKg,
    checkedBaggageWeightKg: one.checkedKg,
    carryOnSourcePath: one.carryKgPath,
    checkedSourcePath: one.checkedKgPath,
  };
  const copy = buildHebrewCopy({
    carryOnIncluded,
    checkedBaggageIncluded,
    carryOnWeightKg: one.carryKg,
    checkedBaggageWeightKg: one.checkedKg,
  });
  return { ...merged, ...copy };
}

/**
 * שורות קצרות לערבוב בסיכום הזמנה (ללא תו כדור).
 */
export function vacationPackageIncludedBaggageDisplayLines(linkedFlightsData) {
  const r = resolveVacationPackageIncludedBaggage({
    linkedFlights: linkedFlightsData,
  });
  if (!r.carryOnIncluded && !r.checkedBaggageIncluded) return [];
  return r.summaryLabel ? [r.summaryLabel] : [];
}

function pickPackageFlightIds(fullDealLike, linkedRoot) {
  const deal = fullDealLike?.fullDeal ?? fullDealLike ?? {};
  const packageId =
    deal.dealId ?? deal.ID ?? deal.packageId ?? deal.id ?? deal?.hotel?.dealId ?? null;
  const flightId =
    linkedRoot?.ldsDealId ??
    linkedRoot?.dealId ??
    linkedRoot?.ldsDealID ??
    null;
  return { packageId, flightId };
}

/**
 * @param {object} input — { linkedFlights } | deal עם linkedFlights
 * @param {{ emitTrace?: boolean }} opts
 */
export function resolveVacationPackageIncludedBaggage(input, opts = {}) {
  const linkedRoot = resolveLinkedFlightsRoot(input);
  const empty = () => ({
    carryOnIncluded: false,
    checkedBaggageIncluded: false,
    checkedBaggageWeight: null,
    carryOnWeightKg: null,
    perPassenger: true,
    summaryLabel: "ללא כבודה כלולה בכרטיס",
    bullets: ["ללא כבודה"],
    hasAnyIncluded: false,
    sourcePath: null,
  });

  if (!linkedRoot) {
    return empty();
  }

  const pairs = getDepRetFlightPairs(linkedRoot);
  const depA = pairs.dep ? analyzeOneDirection(pairs.dep.leg, pairs.dep.wrapper) : null;
  const retA = pairs.ret ? analyzeOneDirection(pairs.ret.leg, pairs.ret.wrapper) : null;

  const merged = mergeDirectionsPair(depA, retA);
  const copy = buildHebrewCopy(merged);

  const out = {
    carryOnIncluded: merged.carryOnIncluded,
    checkedBaggageIncluded: merged.checkedBaggageIncluded,
    checkedBaggageWeight: merged.checkedBaggageWeightKg,
    carryOnWeightKg: merged.carryOnWeightKg,
    perPassenger: true,
    summaryLabel: copy.summaryLabel,
    bullets: copy.bullets,
    hasAnyIncluded: merged.carryOnIncluded || merged.checkedBaggageIncluded,
    sourcePath: merged.sourcePath,
  };

  const wantTrace = opts.emitTrace === true;

  if (wantTrace && typeof console !== "undefined" && console.log) {
    const { packageId, flightId } = pickPackageFlightIds(input, linkedRoot);
    console.log("[VACATION_PACKAGE_INCLUDED_BAGGAGE_TRACE]", {
      packageId,
      flightId,
      carryOnIncluded: out.carryOnIncluded,
      checkedIncluded: out.checkedBaggageIncluded,
      weight: out.checkedBaggageWeight,
      carryOnWeight: out.carryOnWeightKg,
      sourcePath: out.sourcePath,
    });
  }

  return out;
}
